import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { folderLabel, type MediaAgent } from "../../../shared/media";
import { useMe } from "../../data/queries";
import { Icon } from "../../ui/Icon";

// On the PC, Review talks straight to the agent's review server (127.0.0.1):
// previews and results come from the PC's catalog and never pass through
// Kinmantle's servers. The agent's key reaches only signed-in adults (the
// overview is admin-only). Chrome asks once before the page may connect.
// Anywhere else (a phone away from home), it goes through Kinmantle at
// /api/media/review, which reaches the PC over its Cloudflare Tunnel; photos
// pass through Cloudflare there but aren't stored (the household's choice).
//
// Privacy is decided on the PC (agent/kinmantle_agent/privacy.py): anything flagged,
// and anything very like a private photo, is kept private automatically. Reviewing is
// only for rescuing photos; approving a folder lets its other photos on the wall.

interface ReviewItem {
  sha: string;
  path: string;
  copies: number;
  kind: string;
  takenAt: string | null;
  takenSource: string | null;
  dateConflict: string | null;
  camera: string | null;
  thumb: string | null;
  tags: { tag: string; score: number }[];
  flags: { category: string; score: number }[];
  caption: string | null;
  faces: number;
  group: { id: number; best: boolean; reason: string } | null;
  decisions: Record<string, string>;
  privacy: { state: "private" | "fine"; by: "auto" | "you"; reason: string } | null;
}

interface Summary {
  sample: number;
  private: number;
  privateAuto: number;
  privateYou: number;
  fineYou: number;
  folders: number;
  foldersApproved: number;
  wallEligible: number;
  skippedVideos: number;
  groups: number;
  groupsOpen: number;
  conflicts: number;
  analysed: number;
}

interface Folder {
  folder: string;
  files: number;
  checked: number;
  private: number;
  skippedVideos: number;
  fine: number;
  eligible: number;
  wouldShow: number;
  approved: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
}

type QueueName = "sample" | "private" | "groups" | "conflicts" | "folders";
const PAGE = 48;
const VIA_KINMANTLE = { url: "/api/media/review", key: "" };

/** Through Kinmantle (no key: the Worker adds it), requests need the site's CSRF header instead. */
function agentFetch<T>(agent: { url: string; key: string }, path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const auth: Record<string, string> = agent.key ? { "x-review-key": agent.key } : init.method && init.method !== "GET" ? { "x-kinmantle": "1" } : {};
  return fetch(`${agent.url}${path}`, {
    method: init.method ?? "GET",
    headers: { ...auth, ...(init.body ? { "content-type": "application/json" } : {}) },
    body: init.body ? JSON.stringify(init.body) : undefined,
    // No targetAddressSpace: http://127.0.0.1 is already a trustworthy origin, Chrome still shows its
    // local-network prompt without it, and a value a browser doesn't know makes fetch() throw outright.
  }).then(async (r) => {
    if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error ?? `Agent answered ${r.status}`);
    return (await r.json()) as T;
  });
}

/** On a Windows PC, try the agent on this machine first; elsewhere go straight through Kinmantle. */
function firstRoute(info: NonNullable<MediaAgent["info"]>["review"]): { url: string; key: string } | null {
  if (!info) return null;
  const maybeThePc = /Windows/.test(navigator.userAgent);
  if (maybeThePc || !info.remote) return { url: info.url, key: info.key };
  return VIA_KINMANTLE;
}

export function MediaReview({ agent }: { agent: MediaAgent }) {
  const info = agent.info?.review ?? null;
  const [review, setReview] = useState(() => firstRoute(info));
  const [queue, setQueue] = useState<QueueName>("sample");
  const summary = useQuery({
    queryKey: ["media-review", "summary", review?.url],
    queryFn: async () => {
      try {
        return await agentFetch<Summary>(review!, "/v1/summary");
      } catch (e) {
        // Not on the PC after all: fall back to the tunnel, if the PC has one.
        if (review?.key && info?.remote) setReview(VIA_KINMANTLE);
        throw e;
      }
    },
    enabled: !!review,
    retry: false,
    refetchInterval: 20_000,
  });

  if (!review) return null;
  if (summary.isPending) return <p className="muted small">Connecting to the agent on this PC…</p>;
  if (summary.isError)
    return (
      <div className="media-pair">
        {review.key ? (
          <>
            <p>
              <strong>Review works on {agent.name}.</strong> Open this page on that PC to see the photos. They're shown straight from the PC and never leave it.
            </p>
            <p className="muted small">
              On {agent.name} and it still says this? Allow "access other apps and services on this device" if your browser asks, and check the ember icon is by the clock.
            </p>
          </>
        ) : (
          <p>
            <strong>Couldn't reach {agent.name}.</strong> Review away from home works while that PC is on and signed in, with the ember icon by the clock.
          </p>
        )}
        <div className="row">
          <button className="btn btn-sm" onClick={() => summary.refetch()} disabled={summary.isFetching}>
            Try again
          </button>
          <span className="muted small">({summary.error.message})</span>
        </div>
      </div>
    );

  const s = summary.data;
  const tabs: { id: QueueName; label: string; count: string }[] = [
    { id: "sample", label: "Sample results", count: s.sample.toLocaleString() },
    { id: "private", label: "Kept private", count: s.private.toLocaleString() },
    { id: "folders", label: "Folders for the wall", count: `${s.foldersApproved} of ${s.folders}` },
    { id: "groups", label: "Look-alikes", count: s.groupsOpen.toLocaleString() },
    { id: "conflicts", label: "Date conflicts", count: s.conflicts.toLocaleString() },
  ];
  return (
    <section className="media-review" id="media-review">
      <h3 className="media-review-title">Review photos</h3>
      <p className="muted small">
        {review.key ? `Shown from ${agent.name}; nothing here is uploaded.` : `Shown from ${agent.name} through Cloudflare's tunnel; nothing is stored on the way.`} Decisions are kept on the PC with the
        catalog. Nothing is moved or deleted.
      </p>
      <p className="small">
        <strong>{s.private.toLocaleString()}</strong> kept private ({s.privateAuto.toLocaleString()} automatically, {s.privateYou.toLocaleString()} by you)
        {s.fineYou > 0 && <> · {s.fineYou.toLocaleString()} you let out</>}
        {s.skippedVideos > 0 && <> · {s.skippedVideos.toLocaleString()} videos not analysed (the wall shows photos only; they stay private)</>} · {s.foldersApproved} of {s.folders} folders approved for the wall ·{" "}
        {s.wallEligible.toLocaleString()} photos could go on it
      </p>
      <div className="chip-row wrap" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} role="tab" aria-selected={queue === t.id} className={`chip ${queue === t.id ? "on" : ""}`} onClick={() => setQueue(t.id)}>
            {t.label} <span className="muted">{t.count}</span>
          </button>
        ))}
      </div>
      {queue === "groups" ? <GroupQueue review={review} /> : queue === "folders" ? <FolderQueue review={review} /> : <ItemQueue review={review} queue={queue} />}
    </section>
  );
}

function useDecide(review: { url: string; key: string }) {
  const qc = useQueryClient();
  const me = useMe();
  return (shas: string[], kind: string, value: string) =>
    agentFetch(review, "/v1/decide", { method: "POST", body: { shas, kind, value, by: me.data?.member.displayName ?? "" } }).then(() =>
      qc.invalidateQueries({ queryKey: ["media-review"] }),
    );
}

function ItemQueue({ review, queue }: { review: { url: string; key: string }; queue: Exclude<QueueName, "groups" | "folders"> }) {
  const q = useInfiniteQuery({
    queryKey: ["media-review", "queue", queue],
    queryFn: ({ pageParam }) => agentFetch<{ total: number; items: ReviewItem[] }>(review, `/v1/queue/${queue}?offset=${pageParam}&limit=${PAGE}`),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (pages.length * PAGE < last.total ? pages.length * PAGE : undefined),
  });
  const decide = useDecide(review);
  const [big, setBig] = useState<ReviewItem | null>(null);
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  if (q.isPending) return <p className="muted small">Loading…</p>;
  if (q.isError) return <p className="error-text">{q.error.message}</p>;
  if (!items.length) return <p className="muted small">{queue === "private" ? "Nothing is kept private yet." : queue === "sample" ? "Run Analyze sample first." : "Nothing here."}</p>;
  return (
    <>
      {queue === "private" && (
        <p className="small">
          These are <strong>already kept private</strong>: the AI flagged them, or they look very like a photo that's private. They never go on the wall. You don't need
          to go through them; look only if you want to rescue one. "Fine for the wall" lets out that one photo (and its identical copies), never its look-alikes.
        </p>
      )}
      <div className="review-grid">
        {items.map((it) => (
          <ReviewCard key={it.sha} it={it} review={review} onOpen={() => setBig(it)}>
            <div className="row">
              <button className={`btn btn-sm ${it.privacy?.state === "fine" ? "btn-primary" : ""}`} onClick={() => decide([it.sha], "flag", "ok")}>
                Fine for the wall
              </button>
              <button className={`btn btn-sm ${it.privacy?.by === "you" && it.privacy.state === "private" ? "btn-primary" : ""}`} onClick={() => decide([it.sha], "flag", "private")}>
                Keep private
              </button>
            </div>
            {queue === "conflicts" && (
              <button className={`btn btn-sm ${it.decisions.date === "ok" ? "btn-primary" : ""}`} onClick={() => decide([it.sha], "date", "ok")}>
                Date is right
              </button>
            )}
          </ReviewCard>
        ))}
      </div>
      {q.hasNextPage && (
        <button className="btn" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
          Show more ({(q.data.pages[0]!.total - items.length).toLocaleString()} left)
        </button>
      )}
      {big && <BigView it={big} review={review} onClose={() => setBig(null)} />}
    </>
  );
}

function GroupQueue({ review }: { review: { url: string; key: string } }) {
  const q = useInfiniteQuery({
    queryKey: ["media-review", "queue", "groups"],
    queryFn: ({ pageParam }) => agentFetch<{ total: number; groups: { id: number; items: ReviewItem[] }[] }>(review, `/v1/queue/groups?offset=${pageParam}&limit=12`),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (pages.length * 12 < last.total ? pages.length * 12 : undefined),
  });
  const decide = useDecide(review);
  const [big, setBig] = useState<ReviewItem | null>(null);
  const groups = q.data?.pages.flatMap((p) => p.groups) ?? [];
  if (q.isPending) return <p className="muted small">Loading…</p>;
  if (q.isError) return <p className="error-text">{q.error.message}</p>;
  if (!groups.length) return <p className="muted small">No look-alikes found.</p>;
  return (
    <>
      {groups.map((g) => {
        const shas = g.items.map((i) => i.sha);
        const decided = g.items[0]?.decisions.similar;
        return (
          <div key={g.id} className="review-group">
            <div className="review-group-head">
              <strong>{g.items[0]?.group?.reason === "near-duplicate" ? "Same picture" : "Taken together, look alike"}</strong>
              <span className="muted small">{g.items.length} photos · ★ = the one to keep in view (sharpest)</span>
              <span className="spacer" />
              <button className={`btn btn-sm ${decided === "keep" ? "btn-primary" : ""}`} onClick={() => decide(shas, "similar", "keep")}>
                Looks right
              </button>
              <button className={`btn btn-sm ${decided === "separate" ? "btn-primary" : ""}`} onClick={() => decide(shas, "similar", "separate")}>
                Not look-alikes
              </button>
            </div>
            <div className="review-strip">
              {g.items.map((it) => (
                <button key={it.sha} className="review-thumb" onClick={() => setBig(it)} title={it.path}>
                  <Thumb it={it} review={review} />
                  {it.group?.best && <span className="review-star">★</span>}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {q.hasNextPage && (
        <button className="btn" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
          Show more groups
        </button>
      )}
      {big && <BigView it={big} review={review} onClose={() => setBig(null)} />}
    </>
  );
}

function FolderQueue({ review }: { review: { url: string; key: string } }) {
  const qc = useQueryClient();
  const me = useMe();
  const q = useQuery({ queryKey: ["media-review", "folders"], queryFn: () => agentFetch<{ folders: Folder[] }>(review, "/v1/folders") });
  const [busy, setBusy] = useState<string | null>(null);
  const set = (folder: string, approved: boolean) => {
    setBusy(folder);
    agentFetch(review, "/v1/folders", { method: "POST", body: { folder, approved, by: me.data?.member.displayName ?? "" } })
      .then(() => qc.invalidateQueries({ queryKey: ["media-review"] }))
      .finally(() => setBusy(null));
  };
  if (q.isPending) return <p className="muted small">Loading…</p>;
  if (q.isError) return <p className="error-text">{q.error.message}</p>;
  return (
    <>
      <p className="small">
        Approve a folder to let its photos go on the family wall. Photos kept private stay off it, even in an approved folder, and so do photos the AI hasn't checked
        yet. Nothing is moved or deleted.
      </p>
      <table className="table">
        <thead>
          <tr>
            <th>Folder</th>
            <th className="num">Photos and videos</th>
            <th className="num">Checked by AI</th>
            <th className="num">Kept private</th>
            <th className="num">Could go on the wall</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {q.data.folders.map((f) => (
            <tr key={f.folder}>
              <td title={f.folder}>{f.folder ? folderLabel(f.folder) : "(top level)"}</td>
              <td className="num">{f.files.toLocaleString()}</td>
              <td className="num">{f.checked.toLocaleString()}</td>
              <td className="num" title={f.skippedVideos ? `Includes ${f.skippedVideos.toLocaleString()} videos, which aren't analysed and never go on the wall` : undefined}>
                {f.private.toLocaleString()}
              </td>
              <td className="num">{f.wouldShow.toLocaleString()}</td>
              <td>
                {f.approved ? (
                  <button className="btn btn-sm btn-primary" disabled={busy === f.folder} onClick={() => set(f.folder, false)} title={`Approved by ${f.approvedBy || "someone"}; click to withdraw`}>
                    Approved
                  </button>
                ) : (
                  <button className="btn btn-sm" disabled={busy === f.folder} onClick={() => set(f.folder, true)}>
                    Approve folder
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Thumb({ it, review }: { it: ReviewItem; review: { url: string; key: string } }) {
  if (!it.thumb) return <div className="review-noimg">No preview</div>;
  const src = `${review.url}/v1/img/${it.thumb}.jpg${review.key ? `?k=${encodeURIComponent(review.key)}` : ""}`;
  return <img src={src} alt={it.caption ?? it.path} loading="lazy" />;
}

function where(path: string): string {
  const parts = path.split("\\");
  return [folderLabel(parts[0] ?? ""), ...parts.slice(1, -1)].slice(-2).join(" › ");
}

function ReviewCard({ it, review, onOpen, children }: { it: ReviewItem; review: { url: string; key: string }; onOpen: () => void; children?: React.ReactNode }) {
  const decide = useDecide(review);
  return (
    <div className="review-card">
      <button className="review-thumb" onClick={onOpen} title="Show larger">
        <Thumb it={it} review={review} />
        {it.kind === "video" && (
          <span className="review-video">
            <Icon name="play" size="0.9rem" />
          </span>
        )}
      </button>
      <div className="review-meta">
        <span className="muted small" title={it.path}>
          {where(it.path)}
          {it.takenAt ? ` · ${it.takenAt.slice(0, 10)}` : ""}
        </span>
        {it.caption && <p className="review-caption">{it.caption}</p>}
        {it.privacy && (
          <span className={`small ${it.privacy.state === "private" ? "warn-text" : "muted"}`}>
            {it.privacy.state === "private" ? (it.privacy.by === "you" ? "Private (you)" : "Private (automatic)") : "Fine for the wall (you)"}
            {it.privacy.by === "auto" ? `: ${it.privacy.reason}` : ""}
          </span>
        )}
        {it.flags.length > 0 && (
          <div className="chip-row wrap">
            {it.flags.map((f) => (
              <span key={f.category} className="chip chip-sm review-flag">
                {f.category}
              </span>
            ))}
          </div>
        )}
        {it.tags.length > 0 && (
          <div className="chip-row wrap">
            {it.tags.map((t) => (
              <span key={t.tag} className="chip chip-sm" title={`score ${t.score}`}>
                {t.tag}
                <button className="review-x" aria-label={`${t.tag} is wrong`} title="Wrong tag" onClick={() => decide([it.sha], "tag", `wrong:${t.tag}`)}>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        {it.dateConflict && <span className="warn-text small">{it.dateConflict}</span>}
        {children}
      </div>
    </div>
  );
}

function BigView({ it, review, onClose }: { it: ReviewItem; review: { url: string; key: string }; onClose: () => void }) {
  return (
    <div className="review-big" role="dialog" aria-modal onClick={onClose}>
      <div className="review-big-inner" onClick={(e) => e.stopPropagation()}>
        <Thumb it={it} review={review} />
        <div className="review-big-side">
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" size="1.2rem" />
          </button>
          <dl className="kv small">
            <dt>File</dt>
            <dd style={{ wordBreak: "break-all" }}>{it.path}</dd>
            {it.copies > 1 && (
              <>
                <dt>Copies</dt>
                <dd>{it.copies} identical files</dd>
              </>
            )}
            <dt>Taken</dt>
            <dd>
              {it.takenAt ?? "unknown"} {it.takenSource ? <span className="muted">({it.takenSource})</span> : null}
            </dd>
            {it.camera && (
              <>
                <dt>Camera</dt>
                <dd>{it.camera}</dd>
              </>
            )}
            <dt>Faces</dt>
            <dd>{it.faces}</dd>
          </dl>
          {it.caption && <p>{it.caption}</p>}
          <p className="muted small">{it.tags.map((t) => t.tag).join(" · ")}</p>
          {it.flags.length > 0 && <p className="warn-text small">{it.flags.map((f) => f.category).join(" · ")}</p>}
        </div>
      </div>
    </div>
  );
}
